import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { GiftIcon, ClockIcon, ArrowDownTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';

const MyBenefits = () => {
    const [bonuses, setBonuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showAllBenefits, setShowAllBenefits] = useState(false);
    const [dateRange, setDateRange] = useState({ from: '', to: '' });

    useEffect(() => {
        fetchBonuses();
    }, []);

    const fetchBonuses = async () => {
        try {
            setLoading(true);
            setError(null);
            const empCode = localStorage.getItem('emp_code') || 'EMP8383';
            
            console.log('📍 Fetching bonuses for emp_code:', empCode);
            
            const response = await fetch('/rules/bonus/bonus-rules/', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-EMP-CODE': empCode
                }
            });

            console.log('📍 Response status:', response.status);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            console.log('✅ Bonuses fetched:', data);
            
            const bonusList = Array.isArray(data) ? data : (data?.data || []);
            const activeBonuses = bonusList.filter(b => b.is_active);
            
            console.log('✅ Active bonuses:', activeBonuses);
            setBonuses(activeBonuses);
        } catch (error) {
            console.error('❌ Failed to fetch bonuses:', error);
            setError(error.message);
            setBonuses([]);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
        );
    }

    const downloadBenefitsHistory = () => {
        try {
            const emp_code = localStorage.getItem('emp_code') || 'EMPLOYEE';
            const emp_name = localStorage.getItem('user_name') || 'Employee';

            const metadataRows = [
                { 'Benefits History Report': 'Employee Code', 'Value': emp_code },
                { 'Benefits History Report': 'Employee Name', 'Value': emp_name },
                { 'Benefits History Report': 'Report Type', 'Value': 'My Benefits & Bonus Rules' },
                { 'Benefits History Report': 'Total Records', 'Value': bonuses.length },
                { 'Benefits History Report': 'Generated On', 'Value': new Date().toLocaleString() },
                { 'Benefits History Report': '', 'Value': '' }
            ];

            const dataRows = bonuses.map(bonus => ({
                'Benefit Name': bonus.bonus_name,
                'Code': bonus.bonus_code,
                'Frequency': bonus.frequency,
                'Amount (₹)': bonus.fixed_amount || 'N/A',
                'Status': 'Active'
            }));

            const allRows = [...metadataRows, ...dataRows];
            const ws = XLSX.utils.json_to_sheet(allRows);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Benefits History');
            XLSX.writeFile(wb, `Benefits_History_${new Date().getTime()}.xlsx`);
        } catch (err) {
            console.error('Download error:', err);
            alert('Download failed');
        }
    };

    return (
        <div className="min-h-screen p-4 sm:p-6 lg:p-8 bg-gray-50" style={{ fontFamily: 'Roboto, sans-serif' }}>
            <header className="pb-6 mb-8 border-b border-gray-200">
                <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <GiftIcon className="w-8 h-8 inline-block mr-2 text-green-600"/> My Benefits
                </h1>
                <p className="text-sm text-gray-500 mt-2">View all applicable bonus and benefits</p>
            </header>

            {error && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-800"><strong>Error:</strong> {error}</p>
                </div>
            )}

            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-bold text-gray-800">Benefits History</h2>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setShowHistoryModal(true)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold transition"
                                >
                                    <ClockIcon className="w-3.5 h-3.5" />
                                    View History
                                </button>
                                <button
                                    onClick={downloadBenefitsHistory}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-semibold transition"
                                >
                                    <ArrowDownTrayIcon className="w-3.5 h-3.5" />
                                    Download
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Benefit Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Code</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Frequency</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {bonuses.length > 0 ? (
                                    (showAllBenefits ? bonuses : bonuses.slice(0, 7)).map((bonus) => (
                                        <tr key={bonus.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 text-sm font-semibold text-gray-900">{bonus.bonus_name}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{bonus.bonus_code}</td>
                                            <td className="px-6 py-4 text-sm text-gray-600">{bonus.frequency}</td>
                                            <td className="px-6 py-4 text-sm font-bold text-green-600">₹{bonus.fixed_amount || 'N/A'}</td>
                                            <td className="px-6 py-4">
                                                <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                    Active
                                                </span>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-12 text-center text-gray-400">
                                            <GiftIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                                            <p>No active benefits available</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                        {bonuses.length > 7 && (
                            <div className="p-4 border-t border-gray-200 text-center">
                                <button
                                    onClick={() => setShowAllBenefits(!showAllBenefits)}
                                    className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                                >
                                    {showAllBenefits ? 'Show Less' : `View All (${bonuses.length} benefits)`}
                                </button>
                            </div>
                        )}
                    </div>
                </div>

                {bonuses.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {bonuses.map((bonus) => (
                            <div key={bonus.id} className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm hover:shadow-md transition">
                                <div className="flex items-start justify-between mb-3">
                                    <div>
                                        <h3 className="text-lg font-bold text-gray-900">{bonus.bonus_name}</h3>
                                        <p className="text-xs text-gray-500 mt-1">Code: {bonus.bonus_code}</p>
                                    </div>
                                    <span className="bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full">
                                        Active
                                    </span>
                                </div>
                                
                                <div className="space-y-2 border-t border-gray-100 pt-4">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600">Frequency:</span>
                                        <span className="text-sm font-semibold text-gray-900">{bonus.frequency}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600">Amount:</span>
                                        <span className="text-sm font-bold text-green-600">₹{bonus.fixed_amount || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>

            {showHistoryModal && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl w-full max-w-md p-6 relative shadow-xl">
                        <button
                            onClick={() => {
                                setShowHistoryModal(false);
                                setDateRange({ from: '', to: '' });
                            }}
                            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600"
                        >
                            <XMarkIcon className="w-6 h-6" />
                        </button>
                        <h3 className="text-xl font-bold text-slate-900 mb-2">View Benefits History</h3>
                        <p className="text-sm text-slate-600 mb-6">Select date range to filter benefits records</p>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-semibold text-slate-700 block mb-2">From Date</label>
                                <input
                                    type="date"
                                    value={dateRange.from}
                                    onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-slate-700 block mb-2">To Date</label>
                                <input
                                    type="date"
                                    value={dateRange.to}
                                    onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                                    className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={() => setDateRange({ from: '', to: '' })}
                                    className="flex-1 px-4 py-2 border border-slate-300 rounded-lg text-sm font-semibold hover:bg-slate-50 transition"
                                >
                                    Clear Filter
                                </button>
                                <button
                                    onClick={() => setShowHistoryModal(false)}
                                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
                                >
                                    Apply Filter
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MyBenefits;
